import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { taskService } from '../services/taskService'
import { clientService } from '../services/clientService'
import { shootService } from '../services/shootService'
import type { 
  Task, 
  TaskStats, 
  WeeklyTaskLimits, 
  AITaskSuggestion,
  WeekInfo
} from '../types/task'
import { getWeekInfo, formatWeekRange } from '../types/task'
import type { Client, Shoot } from '../types'
import AppHeader from '../components/AppHeader'
import TaskCard from '../components/TaskCard'
import CreateTaskModal from '../components/CreateTaskModal'
import AITaskSuggestions from '../components/AITaskSuggestions'
import AIInsightsDashboard from '../components/AIInsightsDashboard'
import { 
  ClipboardDocumentListIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  FireIcon
} from '@heroicons/react/24/outline'

const TasksPage = () => {
  const { user } = useAuth()
  const { showSuccess, showError, showConfirm } = useNotification()
  const navigate = useNavigate()
  
  const [tasks, setTasks] = useState<Task[]>([])
  const [stats, setStats] = useState<TaskStats | null>(null)
  const [weeklyLimits, setWeeklyLimits] = useState<WeeklyTaskLimits | null>(null)
  const [clients, setClients] = useState<Client[]>([])
  const [shoots, setShoots] = useState<Shoot[]>([])
  const [aiSuggestions, setAiSuggestions] = useState<AITaskSuggestion[]>([])
  
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showAISuggestions, setShowAISuggestions] = useState(false)
  const [showAIDashboard, setShowAIDashboard] = useState(false)
  const [selectedWeek, setSelectedWeek] = useState<WeekInfo>(getWeekInfo())

  const isPremiumUser = user?.subscriptionStatus === 'professional' || user?.subscriptionStatus === 'studio'

  useEffect(() => {
    if (user) {
      loadData()
    }
  }, [user, selectedWeek])

  const loadData = async () => {
    if (!user) return
    
    setLoading(true)
    try {
      const [
        tasksResult,
        statsResult,
        limitsResult,
        clientsData,
        shootsData
      ] = await Promise.all([
        taskService.getTasks(user.id, { weekStartDate: selectedWeek.weekStartDate }),
        taskService.getTaskStats(user.id),
        taskService.getWeeklyLimits(user.id, user.subscriptionStatus || 'freemium'),
        clientService.getClientsForUser(user.id),
        shootService.getShootsForUser(user.id)
      ])

      if (tasksResult.success) {
        setTasks(tasksResult.tasks || [])
      }
      if (statsResult.success) {
        setStats(statsResult.stats || null)
      }
      setWeeklyLimits(limitsResult)
      setClients(clientsData)
      setShoots(shootsData)

      // Load AI suggestions for premium users
      if (isPremiumUser) {
        const suggestionsResult = await taskService.getAITaskSuggestions(user.id, user.subscriptionStatus || 'freemium')
        if (suggestionsResult.success) {
          setAiSuggestions(suggestionsResult.suggestions || [])
        }
      }
    } catch (error) {
      console.error('Error loading task data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateTask = async (taskData: any) => {
    if (!user) return
    
    const result = await taskService.createTask(user.id, taskData)
    if (result.success) {
      setShowCreateModal(false)
      showSuccess('Task Created', 'Task created successfully!')
      loadData() // Refresh data
    } else {
      showError('Create Failed', result.error || 'Failed to create task')
    }
  }

  const handleUpdateTask = async (taskId: string, updates: any) => {
    const result = await taskService.updateTask(taskId, updates)
    if (result.success) {
      showSuccess('Task Updated', 'Task updated successfully!')
      loadData() // Refresh data
    } else {
      showError('Update Failed', result.error || 'Failed to update task')
    }
  }

  const handleDeleteTask = async (taskId: string) => {
    showConfirm(
      'Delete Task',
      'Are you sure you want to delete this task? This action cannot be undone.',
      () => performDeleteTask(taskId),
      undefined,
      'Yes, Delete',
      'Cancel'
    )
  }

  const performDeleteTask = async (taskId: string) => {
    const result = await taskService.deleteTask(taskId)
    if (result.success) {
      showSuccess('Task Deleted', 'Task deleted successfully!')
      loadData() // Refresh data
    } else {
      showError('Delete Failed', result.error || 'Failed to delete task')
    }
  }

  const handleAcceptAISuggestion = async (suggestion: AITaskSuggestion) => {
    if (!user) return
    
    const result = await taskService.createTaskFromAISuggestion(user.id, suggestion)
    if (result.success) {
      setAiSuggestions(prev => prev.filter(s => s.id !== suggestion.id))
      showSuccess('Task Created', 'Task created from AI suggestion!')
      loadData() // Refresh data
    } else {
      showError('Creation Failed', result.error || 'Failed to create task from suggestion')
    }
  }

  const navigateWeek = (direction: 'prev' | 'next') => {
    // Parse the date string properly to avoid timezone issues
    const [year, month, day] = selectedWeek.weekStartDate.split('-').map(Number)
    const currentDate = new Date(year, month - 1, day) // month is 0-indexed
    
    // Add or subtract 7 days
    const newDate = new Date(currentDate)
    newDate.setDate(currentDate.getDate() + (direction === 'next' ? 7 : -7))
    
    const newWeekInfo = getWeekInfo(newDate)
    setSelectedWeek(newWeekInfo)
  }

  // Priority order for sorting
  const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 }

  const sortTasksByPriority = (tasks: Task[]) => {
    return [...tasks].sort((a, b) => {
      // First sort by priority (urgent -> high -> medium -> low)
      const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority]
      if (priorityDiff !== 0) return priorityDiff
      
      // Then sort by creation date (newest first)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
  }

  const filteredTasks = tasks.filter(task => {
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase()
      return task.title.toLowerCase().includes(searchLower) ||
             task.description?.toLowerCase().includes(searchLower) ||
             task.tags?.some(tag => tag.toLowerCase().includes(searchLower))
    }
    return true
  })

  const tasksByStatus = {
    pending: sortTasksByPriority(filteredTasks.filter(task => task.status === 'pending')),
    in_progress: sortTasksByPriority(filteredTasks.filter(task => task.status === 'in_progress')),
    completed: sortTasksByPriority(filteredTasks.filter(task => task.status === 'completed')),
    cancelled: sortTasksByPriority(filteredTasks.filter(task => task.status === 'cancelled'))
  }

  const overdueTasks = sortTasksByPriority(filteredTasks.filter(task => 
    task.dueDate && 
    new Date(task.dueDate) < new Date() && 
    task.status !== 'completed'
  ))

  if (loading) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
        </div>
        <div className="glass-mockup rounded-3xl p-8">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-white mt-4">Loading tasks...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader 
        title="Task Management"
        subtitle={`Week of ${formatWeekRange(selectedWeek.weekStartDate)} • ${filteredTasks.length} tasks`}
      >
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/dashboard')}
          className="flex items-center space-x-3 text-white hover:text-gray-300 transition-colors"
        >
          <ArrowLeftIcon className="w-6 h-6" />
          <span className="text-lg font-medium">Back to Dashboard</span>
        </motion.button>
      </AppHeader>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Weekly Limits Warning */}
        {weeklyLimits && !weeklyLimits.isUnlimited && weeklyLimits.currentWeekUsage >= weeklyLimits.maxTasksPerWeek * 0.8 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-mockup rounded-2xl p-4 mb-6 border-orange-500/30 bg-orange-500/10"
          >
            <div className="flex items-center space-x-3">
              <ExclamationTriangleIcon className="w-6 h-6 text-orange-400" />
              <div className="flex-1">
                <p className="text-orange-400 font-medium">
                  {weeklyLimits.currentWeekUsage >= weeklyLimits.maxTasksPerWeek 
                    ? 'Weekly task limit reached!' 
                    : `Approaching weekly limit: ${weeklyLimits.currentWeekUsage}/${weeklyLimits.maxTasksPerWeek} tasks used`
                  }
                </p>
                <p className="text-orange-300 text-sm">
                  Upgrade to Pro for unlimited tasks and AI-powered automation
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={() => navigate('/upgrade')}
                className="px-4 py-2 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 rounded-xl transition-colors border border-orange-500/30"
              >
                Upgrade Now
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Stats Cards */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8"
          >
            <div className="glass-mockup rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <ClipboardDocumentListIcon className="w-6 h-6 text-blue-400" />
                <span className="text-2xl font-bold text-white">{stats.thisWeekTasks}</span>
              </div>
              <p className="text-gray-400 text-sm font-medium">This Week</p>
            </div>

            <div className="glass-mockup rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <CheckCircleIcon className="w-6 h-6 text-green-400" />
                <span className="text-2xl font-bold text-white">{stats.thisWeekCompleted}</span>
              </div>
              <p className="text-gray-400 text-sm font-medium">Completed</p>
            </div>

            <div className="glass-mockup rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <ClockIcon className="w-6 h-6 text-orange-400" />
                <span className="text-2xl font-bold text-white">{overdueTasks.length}</span>
              </div>
              <p className="text-gray-400 text-sm font-medium">Overdue</p>
            </div>

            <div className="glass-mockup rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <FireIcon className="w-6 h-6 text-purple-400" />
                <span className="text-2xl font-bold text-white">{stats.productivityScore}%</span>
              </div>
              <p className="text-gray-400 text-sm font-medium">Productivity</p>
            </div>
          </motion.div>
        )}

        {/* Week Navigation & Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-mockup rounded-2xl p-6 mb-8"
        >
          {/* Weekly Limit Info for Free Users */}
          {weeklyLimits && !weeklyLimits.isUnlimited && (
            <div className="mb-4 p-3 rounded-xl bg-blue-500/10 border border-blue-500/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <ClipboardDocumentListIcon className="w-5 h-5 text-blue-400" />
                  <div>
                    <p className="text-blue-400 font-medium text-sm">
                      Free Plan: {weeklyLimits.currentWeekUsage}/{weeklyLimits.maxTasksPerWeek} tasks this week
                    </p>
                    <p className="text-blue-300 text-xs">
                      {weeklyLimits.maxTasksPerWeek - weeklyLimits.currentWeekUsage} tasks remaining
                    </p>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  onClick={() => navigate('/upgrade')}
                  className="px-3 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors text-sm"
                >
                  Upgrade for Unlimited
                </motion.button>
              </div>
            </div>
          )}
          
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Week Navigation */}
            <div className="flex items-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigateWeek('prev')}
                className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </motion.button>
              
              <div className="text-center">
                <h3 className="text-white font-semibold">
                  {formatWeekRange(selectedWeek.weekStartDate)}
                </h3>
                <p className="text-gray-400 text-sm">
                  Week {selectedWeek.weekNumber}, {selectedWeek.year}
                  {selectedWeek.isCurrentWeek && ' (Current)'}
                </p>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigateWeek('next')}
                className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <ArrowRightIcon className="w-5 h-5" />
              </motion.button>
            </div>

            {/* Search & Actions */}
            <div className="flex items-center space-x-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {isPremiumUser && aiSuggestions.length > 0 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  onClick={() => setShowAISuggestions(true)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all border ${
                    aiSuggestions[0]?.isLearningNotice
                      ? 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 hover:from-blue-500/30 hover:to-cyan-500/30 text-blue-400 border-blue-500/30'
                      : 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 text-purple-400 border-purple-500/30'
                  }`}
                >
                  <SparklesIcon className="w-5 h-5" />
                  <span>
                    {aiSuggestions[0]?.isLearningNotice 
                      ? 'AI Learning Mode' 
                      : `AI Suggestions (${aiSuggestions.length})`
                    }
                  </span>
                </motion.button>
              )}

              {isPremiumUser && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  onClick={() => setShowAIDashboard(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 hover:from-blue-500/30 hover:to-purple-500/30 text-blue-400 rounded-xl transition-all border border-blue-500/30"
                >
                  <ChartBarIcon className="w-5 h-5" />
                  <span>AI Insights</span>
                </motion.button>
              )}

              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={() => setShowCreateModal(true)}
                disabled={weeklyLimits ? !weeklyLimits.canCreateTask : false}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all ${
                  weeklyLimits && !weeklyLimits.canCreateTask
                    ? 'bg-gray-500/20 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white'
                }`}
              >
                <PlusIcon className="w-5 h-5" />
                <span>New Task</span>
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Task Columns */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 lg:grid-cols-4 gap-6"
        >
          {/* Pending Tasks */}
          <div className="glass-mockup rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-semibold">Pending</h3>
              <span className="px-2 py-1 bg-gray-500/20 text-gray-400 rounded-full text-sm">
                {tasksByStatus.pending.length}
              </span>
            </div>
            <div className="space-y-4">
              {tasksByStatus.pending.map((task, index) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  index={index}
                  onUpdate={handleUpdateTask}
                  onDelete={handleDeleteTask}
                  clients={clients}
                  shoots={shoots}
                />
              ))}
              {tasksByStatus.pending.length === 0 && (
                <p className="text-gray-500 text-center py-8">No pending tasks</p>
              )}
            </div>
          </div>

          {/* In Progress Tasks */}
          <div className="glass-mockup rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-semibold">In Progress</h3>
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">
                {tasksByStatus.in_progress.length}
              </span>
            </div>
            <div className="space-y-4">
              {tasksByStatus.in_progress.map((task, index) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  index={index}
                  onUpdate={handleUpdateTask}
                  onDelete={handleDeleteTask}
                  clients={clients}
                  shoots={shoots}
                />
              ))}
              {tasksByStatus.in_progress.length === 0 && (
                <p className="text-gray-500 text-center py-8">No tasks in progress</p>
              )}
            </div>
          </div>

          {/* Completed Tasks */}
          <div className="glass-mockup rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-semibold">Completed</h3>
              <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                {tasksByStatus.completed.length}
              </span>
            </div>
            <div className="space-y-4">
              {tasksByStatus.completed.map((task, index) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  index={index}
                  onUpdate={handleUpdateTask}
                  onDelete={handleDeleteTask}
                  clients={clients}
                  shoots={shoots}
                />
              ))}
              {tasksByStatus.completed.length === 0 && (
                <p className="text-gray-500 text-center py-8">No completed tasks</p>
              )}
            </div>
          </div>

          {/* Cancelled Tasks */}
          <div className="glass-mockup rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-semibold">Cancelled</h3>
              <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-sm">
                {tasksByStatus.cancelled.length}
              </span>
            </div>
            <div className="space-y-4">
              {tasksByStatus.cancelled.map((task, index) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  index={index}
                  onUpdate={handleUpdateTask}
                  onDelete={handleDeleteTask}
                  clients={clients}
                  shoots={shoots}
                />
              ))}
              {tasksByStatus.cancelled.length === 0 && (
                <p className="text-gray-500 text-center py-8">No cancelled tasks</p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Empty State */}
        {filteredTasks.length === 0 && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-mockup rounded-2xl p-12 text-center mt-8"
          >
            <ClipboardDocumentListIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              {searchQuery ? 'No tasks found' : 'No tasks for this week'}
            </h3>
            <p className="text-gray-400 mb-6">
              {searchQuery 
                ? 'Try adjusting your search terms'
                : 'Start organizing your week by creating your first task'
              }
            </p>
            {!searchQuery && weeklyLimits?.canCreateTask && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={() => setShowCreateModal(true)}
                className="btn-primary"
              >
                Create Your First Task
              </motion.button>
            )}
          </motion.div>
        )}
      </main>

      {/* Create Task Modal */}
      <CreateTaskModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateTask}
        clients={clients}
        shoots={shoots}
        weeklyLimits={weeklyLimits}
      />

      {/* AI Suggestions Modal */}
      {isPremiumUser && (
        <AITaskSuggestions
          isOpen={showAISuggestions}
          onClose={() => setShowAISuggestions(false)}
          suggestions={aiSuggestions}
          onAccept={handleAcceptAISuggestion}
          onDismiss={(suggestionId: string) => setAiSuggestions(prev => prev.filter(s => s.id !== suggestionId))}
        />
      )}

      {/* AI Insights Dashboard */}
      {isPremiumUser && (
        <AIInsightsDashboard
          isOpen={showAIDashboard}
          onClose={() => setShowAIDashboard(false)}
          onAcceptSuggestion={handleAcceptAISuggestion}
        />
      )}
    </div>
  )
}

export default TasksPage 